<template>
  <order-file-uploader
    :drop-target="dropTarget"
    @upload-done="onUploadDone"
    @files-added="onFilesAdded"
    @file-progress="onFileProgress"
    @file-removed="onFileRemoved"
    @drag-over="dragGlow = true"
    @drag-leave="dragGlow = false"
  >
    <section
      class="upload-menu"
      :class="{
        glow: rootStore.uploadInProgress,
        'hover-glow': dragGlow,
        'order-menu': $route.name !== 'home'
      }"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <transition name="menu-fade" mode="out-in">
        <div class="content-container" v-if="!uploadMenu">
          <div class="title-container">
            <h1
              v-if="$route.name === 'home' && rootStore.globe.mainTitle"
              v-html="rootStore.globe.mainTitle"
              :style="{ color: rootStore.globe.mainTitleColor }"
            ></h1>
            <h1
              v-else-if="showCount"
              :style="{
                color: rootStore.globe.mainTitleColor,
                'padding-top': '19px'
              }"
            >
              Winkelwagen
            </h1>
          </div>

          <div class="globe-content">
            <template v-if="showCount">
              <p
                :style="[
                  totalFiles === 0
                    ? { color: '#bbbbbb' }
                    : { color: rootStore.globe.mainTextColor }
                ]"
                class="file-count pointer"
                @click="goToCart"
              >
                {{ totalFiles }}
              </p>
            </template>
            <template v-else-if="rootStore.uploadInProgress">
              <p
                :style="{ color: rootStore.globe.mainTextColor }"
                class="progress"
              >
                {{ rootStore.uploadProgress }}%
              </p>
            </template>
            <template v-else-if="isInPrintOrderV2">
              <p class="no-files">
                U heeft nog<br />geen bestanden<br />geupload.
              </p>
            </template>
            <template v-else>
              <h2
                class="upload-title"
                :style="{ color: rootStore.globe.mainTextColor }"
              >
                Sleep hier<br />uw bestanden
              </h2>
            </template>
          </div>
        </div>
        <upload-menu
          v-else
          :printstore="hasPrintStore"
          :filestore="hasFileStore"
          @close="uploadMenu = false"
          v-click-outside:upload-menu-btn="toggleUploadMenu"
        ></upload-menu>
      </transition>

      <div class="btn-holder">
        <uploader-btn
          class="upload-file-btn"
          v-if="!hasFileStore && !hasPrintStore"
        >
          <span
            :style="{ backgroundColor: rootStore.globe.buttonBackgroundColor }"
          ></span>
          <span
            :style="{ backgroundColor: rootStore.globe.buttonBackgroundColor }"
          ></span>
        </uploader-btn>
        <button
          id="upload-menu-btn"
          class="upload-file-btn"
          aria-label="Open upload menu"
          :class="{ close: uploadMenu }"
          @click="toggleUploadMenu"
          v-else
        >
          <span
            :style="{ backgroundColor: rootStore.globe.buttonBackgroundColor }"
          ></span>
          <span
            :style="{ backgroundColor: rootStore.globe.buttonBackgroundColor }"
          ></span>
        </button>
      </div>
    </section>
  </order-file-uploader>
</template>

<script>
import UploadMenu from './UploadMenu.vue'
import InteractionMixin from '@/mixins/InteractionMixin'
import OrderFileUploader from '@/components/ui/file-upload/OrderFileUploader.vue'
import { useFileListStore } from '@/stores/FileList.store'
import { mapStores } from 'pinia'
import { useFileStoreStore } from '@/features/filestore/FileStore.store'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'

export default {
  props: ['progress', 'fileDrag'],
  components: { OrderFileUploader, UploadMenu },
  mixins: [InteractionMixin],
  data() {
    return {
      uploadMenu: false,
      dragGlow: false,
      uploadProgress: 0,
      inProgress: false
    }
  },
  computed: {
    ...mapStores(useFileListStore, useFileStoreStore, usePrintStoreStore),

    hasFileStore() {
      return this.rootStore.interactions.some(
        (ia) => ia.destination === 'fileStore'
      )
    },
    hasPrintStore() {
      return this.rootStore.interactions.some(
        (ia) => ia.destination === 'printStore'
      )
    },
    isFileStore() {
      return this.rootStore.currentSection === 'filestore'
    },
    showCount() {
      return (
        (this.totalFiles > 0 || this.isFileStore) &&
        !this.rootStore.uploadInProgress
      )
    },
    totalFiles() {
      return (
        this.fileListStore.fileList.length +
        this.printStoreStore.orderDetails.cart.length
      )
    },
    dropTarget() {
      if (this.$route.name === 'printOrderCartV2') {
        return ''
      } else {
        return 'app'
      }
    },
    isInPrintOrderV2() {
      const routeName = this.$route.name
      const inPrintOrderFlow = routeName && routeName.startsWith('printOrder')
      const isCartV2Enabled = this.rootStore.theme.customFields.printOrder
        ?.cartV2
      return inPrintOrderFlow && isCartV2Enabled
    }
  },
  watch: {
    'rootStore.uploadInProgress': function (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.uploadMenu = false
      }
    }
  },
  methods: {
    onFilesAdded() {
      this.dragGlow = false
      this.uploadMenu = false
      this.rootStore.uploadInProgress = true
    },
    onFileProgress(value) {
      this.dragGlow = false
      // working around a bug in component here - it passes the wrong value at 100%
      if (!isNaN(value)) {
        this.rootStore.uploadProgress = value
      }
    },
    onUploadDone() {
      this.rootStore.uploadInProgress = false
      this.rootStore.uploadProgress = 0
    },
    onFileRemoved() {
      this.rootStore.uploadInProgress = false
      this.rootStore.uploadProgress = 0
    },
    onDrop() {
      if (this.fileStoreStore.hasSelectedFiles) {
        this.fsDrag = false
      }
    },
    onDragOver() {
      if (this.fileStoreStore.hasSelectedFiles) {
        this.fsDrag = true
      }
    },
    onDragLeave() {
      if (this.fileStoreStore.hasSelectedFiles) {
        this.fsDrag = false
      }
    },
    goToCart() {
      const printOrder =
        this.rootStore.interactions.find(
          (ia) => ia.destination === 'productOrder'
        ) ||
        this.rootStore.interactions.find(
          (ia) => ia.destination === 'printOrder'
        )
      this.startInteraction(printOrder)
    },
    toggleUploadMenu() {
      this.uploadMenu = !this.uploadMenu
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-menu {
  padding-top: 15px;
  transition: box-shadow 0.5s;
  width: 100%;
  height: 100%;
  border-radius: 150px;
  overflow: hidden;

  &.glow {
    animation: pulse 1.5s infinite;
  }

  &.hover-glow {
    box-shadow: 0px 0px 25px 9px;
  }

  .title-container {
    height: 36px;
  }

  .file-count {
    font-weight: 300;
    font-size: 110px;
    line-height: 1.15;
  }

  .progress {
    font-size: 68px;
    font-weight: 300;
    display: table-cell;
    vertical-align: middle;
  }

  h1 {
    font-size: 14px;
    line-height: 19px;
    padding-top: 3px;
    width: 125px;
    margin: 0 auto;
    padding-bottom: 3px;
    font-weight: 500;
  }

  .globe-content {
    height: 134px;
    display: table;
    width: 100%;

    h2 {
      font-size: 19px;
      line-height: 25px;
      display: table-cell;
      vertical-align: middle;
    }
  }
}

.list-icon g {
  fill: inherit;
}

.no-files {
  font-weight: 500;
  padding-top: 31px;
  line-height: 20px;
}

.upload-file-btn {
  width: 25px;
  height: 25px;
  padding: 0;
  border: none;
  float: none;
  position: relative;
  transition: transform 0.35s;

  &:hover {
    background-color: transparent;
  }

  &.close {
    transform: rotate(-45deg);
  }

  span {
    background-color: var(--sub-color);
    height: 3px;
    left: 0;
    position: absolute;
    top: 11px;
    width: 25px;
    display: inline-block;
  }

  span:nth-of-type(2) {
    height: 25px;
    left: 11px;
    top: 0;
    width: 3px;
  }
}

.btn-holder {
  position: absolute;
  left: 0;
  width: 100%;
  bottom: 32px;

  label,
  button {
    margin: 0 9px;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0px 0px 25px 9px;
  }
  50% {
    box-shadow: 0px 0px 15px -3px;
  }
  100% {
    box-shadow: 0px 0px 25px 9px;
  }
}

.content-container {
  height: 100%;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
}
</style>
